import { memo } from 'react'

import { Icons } from 'atoms'
import { DropdownForm } from 'atoms/web/DropdownForm'
import { TNetworks, TUsers } from 'integration/resources/newOpportunities'
import {
  Heading,
  HStack,
  Pressable,
  Text,
  Button,
  Box,
  Menu,
  VStack,
  Modal,
  IconButton
} from 'native-base'
import { Controller } from 'react-hook-form'

import { getOptionLabel } from './constants'
import {
  ResponsiveOpportunityFilterScreenComponent,
  TOption,
  TOptionCommon
} from './OpportunityFilterScreen.types'

const OpportunityFilterScreenDesktop: ResponsiveOpportunityFilterScreenComponent = memo(
  ({
    closeRightSheet,
    control,
    isDirty,
    handleSubmit,
    handleSubmitForm,
    handleResetForm,
    fieldNames,
    fieldConfig,
    isLoading
  }) => {
    return (
      <Modal isOpen onClose={closeRightSheet} safeArea size="xl">
        <Modal.Content
          maxW="640px"
          w="90%"
          borderRadius="20px"
          px={0}
          py={0}
          bgColor="white"
          _dark={{
            bgColor: 'gray.900'
          }}>
          <Modal.Body px={8} pt={8} pb={2}>
            <Box alignItems="flex-end">
              <IconButton
                bg="gray.50"
                _hover={{
                  bg: 'gray.100'
                }}
                _pressed={{
                  bg: 'gray.100'
                }}
                w={10}
                h={10}
                borderRadius="full"
                onPress={closeRightSheet}
                icon={<Icons.Close size={3} color="black" />}
              />
            </Box>

            <HStack space={2} mt={8} mb={6} alignItems="center" justifyContent="space-between">
              <Heading color="gray.dark" fontSize="24px" bold>
                Filtrar
              </Heading>

              <Pressable
                bg="gray.50"
                _pressed={{
                  opacity: 0.7
                }}
                px={7}
                py="10px"
                borderRadius="30px"
                onPress={handleResetForm}>
                <HStack alignItems="center">
                  <Text
                    textAlign="center"
                    mr={3}
                    fontSize="14px"
                    lineHeight="20px"
                    color="gray.700"
                    bold>
                    Limpar
                  </Text>

                  <Icons.Trash color="gray.700" size="18px" />
                </HStack>
              </Pressable>
            </HStack>

            <VStack space={6} pb={4}>
              {fieldNames.map((fieldName) => {
                const currentField = fieldConfig[fieldName]

                return (
                  <Box key={fieldName}>
                    <Heading
                      fontFamily="OpenSans_400Regular"
                      fontWeight="normal"
                      fontSize="16px"
                      color="gray.900"
                      pb={4}>
                      {currentField.label}
                    </Heading>

                    <Controller
                      name={fieldName}
                      control={control}
                      render={({ field: { value, onChange } }) => {
                        const isSelectedItem = (option: TOption) =>
                          typeof option === 'string'
                            ? option === value
                            : ((option as TOptionCommon)?.name ??
                                (option as TNetworks)?.network ??
                                (option as TUsers)?.std_code) === value

                        const selectedValue = currentField?.options?.find(isSelectedItem)

                        const defaultLabel =
                          currentField?.options?.length === 1
                            ? getOptionLabel(currentField?.options[0])
                            : 'Todos'

                        return (
                          <DropdownForm
                            label={selectedValue ? getOptionLabel(selectedValue) : defaultLabel}
                            isDisabled={isLoading}
                            isLoading={isLoading}
                            renderItems={
                              <>
                                {currentField.options.length ? (
                                  currentField.options.map((option) => (
                                    <Menu.Item
                                      key={
                                        typeof option === 'string'
                                          ? option
                                          : (option as TOptionCommon)?.name ??
                                            (option as TNetworks)?.network ??
                                            (option as TUsers)?.std_code
                                      }
                                      backgroundColor="white"
                                      _focus={{ backgroundColor: 'gray.50' }}
                                      _pressed={{ backgroundColor: 'gray.50' }}
                                      _hover={{ backgroundColor: 'gray.50' }}
                                      w="100%"
                                      px={8}
                                      py={4}
                                      mb={2}
                                      borderBottomWidth="1px"
                                      borderColor="gray.50"
                                      onPress={() =>
                                        onChange(
                                          typeof option === 'string'
                                            ? option
                                            : (option as TOptionCommon)?.name ??
                                                (option as TNetworks)?.network ??
                                                (option as TUsers)?.std_code
                                        )
                                      }>
                                      <Text fontSize="16px" lineHeight="24px" color="gray.500">
                                        {getOptionLabel(option)}
                                      </Text>
                                    </Menu.Item>
                                  ))
                                ) : (
                                  <Box px={8} w="100%" pt={5}>
                                    <Text fontSize="16px" lineHeight="24px" color="gray.500">
                                      Nenhuma opção disponível.
                                    </Text>
                                  </Box>
                                )}
                              </>
                            }
                          />
                        )
                      }}
                    />
                  </Box>
                )
              })}
            </VStack>
          </Modal.Body>

          <Modal.Footer borderTopWidth={0} px={8} pb={10} pt={4} bgColor="white">
            <Button
              w="full"
              colorScheme="gray"
              isDisabled={!isDirty || isLoading}
              onPress={handleSubmit(handleSubmitForm)}
              isLoading={isLoading}>
              Ver resultados
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    )
  }
)

export default OpportunityFilterScreenDesktop
